<script setup lang="ts">
/**
 * Строка истории релизов — макет 285:2448.
 *
 * Отдельным компонентом, потому что строка рисуется дважды: плоским списком и
 * внутри группы репозитория. Кнопка выката в двух копиях разъезжалась бы —
 * первый же правленый экран расходился бы со вторым.
 */
import { ExternalLink, Play, Tag } from 'lucide-vue-next'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import PipelineBadge from '@/features/issue/components/PipelineBadge.vue'

import type { Release } from '../gitlab'

const props = defineProps<{
  release: Release
  /** Готовая отметка времени: формат общий на экран, локаль знает он же */
  stamp: string
  /** Плоский список показывает ещё и сервис: репозиторий там не в заголовке */
  withService?: boolean
  /** Право `tracker:devops` — то же, что на постановку тега */
  canDeploy?: boolean
  /** Выкат этого релиза запрашивается прямо сейчас */
  isDeploying?: boolean
}>()

const emit = defineEmits<{ deploy: [release: Release] }>()

const { t } = useI18n()

/**
 * Кнопка есть ровно у одного состояния прогона — `manual`: так GitLab метит
 * пайплайн, упёршийся в ручную джобу, то есть отработавший всё, что стоит
 * перед ней.
 *
 * На упавшем прогоне кнопки нет намеренно. По статусу пайплайна не отличить
 * «упал выкат» от «упали тесты», а предлагать выкатить непрошедшую сборку
 * нельзя: разбираться с такой в GitLab всё равно придётся глазами.
 */
const isWaiting = computed(() => props.release.pipeline?.status === 'manual')

const badge = computed(() => ({
  id: props.release.pipeline?.id ?? '',
  projectPath: props.release.projectPath,
  ref: props.release.tag,
  status: props.release.pipeline?.status ?? '',
  url: props.release.pipeline?.url ?? '',
  durationSeconds: props.release.pipeline?.durationSeconds ?? 0,
}))

const service = computed(() => props.release.projectPath.split('/').pop() ?? '')

/**
 * Подпись собирается выражением, а не двумя вставками с пробелом между ними:
 * компилятор шаблона считает такой пробел незначащим и выкидывает, склеивая
 * «tracker» и «v1.5.0» в одно слово.
 */
const label = computed(() =>
  props.withService ? `${service.value} ${props.release.tag}` : props.release.tag,
)
</script>

<template>
  <div class="releases__row">
    <Tag class="releases__icon" :size="16" />
    <span class="releases__cell releases__cell--tag">{{ label }}</span>
    <span class="releases__cell releases__cell--actor">{{ release.author }}</span>
    <PipelineBadge v-if="release.pipeline" :pipeline="badge" />
    <span v-else class="releases__nobuild">{{ t('devops.releases.noBuild') }}</span>
    <span class="releases__spacer" />

    <!--
      Подпись у кнопки только в подсказке: строка плотная, и «Выкатить на prod»
      выдавливало из неё дату и ссылку. Словами действие называет диалог
      подтверждения — тот, что открывается по нажатию.
    -->
    <button
      v-if="isWaiting && canDeploy"
      type="button"
      class="releases__deploy"
      :disabled="isDeploying"
      :title="t('devops.releases.deploy.action')"
      :aria-label="t('devops.releases.deploy.action')"
      @click="emit('deploy', release)"
    >
      <Play :size="13" />
    </button>

    <span class="releases__cell releases__cell--at">{{ stamp }}</span>
    <a
      v-if="release.webUrl"
      class="releases__link"
      :href="release.webUrl"
      target="_blank"
      rel="noopener"
      :aria-label="t('devops.releases.open')"
      :title="t('devops.releases.open')"
    >
      <ExternalLink :size="15" />
    </a>
  </div>
</template>

<style scoped>
.releases__row {
  display: flex;
  align-items: center;
  gap: 10px;
  height: 48px;
  padding: 0 16px;
  border-bottom: 1px solid var(--color-border);
}

.releases__row:last-child {
  border-bottom: none;
}

.releases__icon {
  flex-shrink: 0;
  color: var(--color-text-muted);
}

.releases__cell {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.releases__cell--tag {
  width: 170px;
  color: var(--color-text-primary);
  font-family: var(--font-family-mono);
  font-size: var(--font-size-body-sm);
}

.releases__cell--actor {
  width: 150px;
  color: var(--color-text-secondary);
  font-size: var(--font-size-body);
}

/* Дата не ужимается: рядом встал бейдж сборки, и первым под нож шло как раз
   время выката — ради него в историю и заходят. */
.releases__cell--at {
  flex-shrink: 0;
  color: var(--color-text-muted);
  font-size: var(--font-size-body-sm);
}

/* «Нет сборки» — не отказ, а «прогона мы не знаем»: тег могли поставить
   раньше, чем зеркало помнит, поэтому тон приглушённый, а не тревожный. */
.releases__nobuild {
  flex-shrink: 0;
  color: var(--color-text-muted);
  font-size: var(--font-size-body-sm);
  white-space: nowrap;
}

.releases__spacer {
  flex: 1;
  min-width: 0;
}

.releases__link {
  display: inline-flex;
  color: var(--color-text-muted);
}

.releases__link:hover {
  color: var(--color-text-primary);
}

/* Кнопка выката: заметная, но не крупнее строки — в списке она встречается на
   одном релизе из десятка, и постоянная плашка перетягивала бы взгляд с
   бейджа, который и объясняет, почему кнопка тут. */
.releases__deploy {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  border-radius: var(--radius-sm);
  background: var(--color-primary);
  color: var(--color-text-inverse);
  cursor: pointer;
}

.releases__deploy:hover:not(:disabled) {
  background: var(--color-primary-hover);
}

.releases__deploy:disabled {
  background: var(--color-text-disabled);
  cursor: default;
}
</style>
